import React, { useEffect, useRef } from "react";
import { DynamicFormContext } from "./DynamicFormContext";
import { getFormState, SESSION_TIMEOUT_SECONDS } from "utils";
import { FormsApi, type DynamicForm, type FormSubmit } from "api";
import { useCookies } from "react-cookie";
import { v4 as uuidv4 } from "uuid";
import { useApi } from "../../hooks/useApi";
type Props = {
  formId: string;
  children?: React.ReactNode;
};

export const DynamicFormContextProvider = ({ formId, children }: Props) => {
  const api = useApi(FormsApi);
  const [form, setForm] = React.useState<DynamicForm>();
  const [session, setSession] = React.useState<
    Partial<FormSubmit> & Pick<FormSubmit, "sessionId">
  >();

  const cookieName = `form-session-${formId}`;
  const [cookies, setCookie] = useCookies([cookieName]);
  const loadedSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    let sessionId = cookies[cookieName];
    if (!sessionId) {
      const newSessionId = uuidv4();
      setCookie(cookieName, newSessionId, {
        path: "/",
        maxAge: SESSION_TIMEOUT_SECONDS,
      });
      sessionId = newSessionId;
    }

    // Already loaded this session — don't overwrite in-progress form state
    if (loadedSessionIdRef.current === sessionId) return;
    loadedSessionIdRef.current = sessionId;

    const getSession = async () => {
      const s = await getFormState(sessionId);
      if (s) {
        setSession(s);
      } else {
        setSession({ documentId: formId, sessionId });
      }
    };
    getSession();
  }, [cookies, cookieName, formId, setCookie]);

  console.log(
    "SESSION LOADED",
    session?.sessionId,
    session?.contactInfo,
    session?.steps,
  );

  useEffect(() => {
    (async () => {
      const response = await api.getForm({ documentId: formId });
      setForm(response);
    })();
  }, [api, formId]);

  if (!form || !session) return null;

  return (
    <>
      <DynamicFormContext.Provider value={{ form, session }}>
        {children}
      </DynamicFormContext.Provider>
    </>
  );
};
